import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import ImmutablePureComponent from 'react-immutable-pure-component'
import { is } from 'immutable'
import videojs from 'video.js'
import { isTouch } from '../utils/is_mobile'
import { isPanoramic, isPortrait, minimumAspectRatio, maximumAspectRatio } from '../utils/media_aspect_ratio'
import { displayMedia } from '../initial_state'
import { CX, MEDIA_SINGLE_MAX_HEIGHT } from '../constants'
import Button from './button'
import SensitiveMediaItem from './sensitive_media_item'
import { parse, format } from 'url'

import '!style-loader!css-loader!video.js/dist/video-js.min.css'
import '!style-loader!css-loader!@silvermine/videojs-quality-selector/dist/css/quality-selector.css'
import Blurhash from './blurhash'
require('@silvermine/videojs-quality-selector')(videojs);

const isTouchable = isTouch()
const CONTROLS_HEIGHT = 40;

window.videoQuality = 'HD';

if (!window.videoVolume) {
  window.videoVolume = localStorage.getItem('volume') || 0.5
}

const getVideoJSOptions = ({
  autoplay = undefined,
  muted = undefined,
  sources = [{}],
  controls = undefined,
  volume,
}) => ({
  autoplay,
  muted,
  sources,
  playbackRates: [0.5, 1, 1.5, 2],
  controls: true,
  volume,
  type: 'video/mp4',
  responsive: true
})

class Video extends ImmutablePureComponent {

  state = {
    changed: false,
    containerWidth: this.props.width,
    revealed: this.props.visible !== undefined ? this.props.visible : (displayMedia !== 'hide_all' && !this.props.sensitive || displayMedia === 'show_all'),
    // Lazy loading state
    isVisible: false,
    videoLoaded: false,
  }

  componentDidMount() {
    // Set up intersection observer for ads
    if (this.props.isAd && this.props.lazyLoad !== false) {
      this.setupIntersectionObserver()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.revealed && !this.state.revealed && !!this.video) {
      this.video.pause()
    }
    if (this.props.autoplay !== prevProps.autoplay && !!this.video) {
      if (this.props.autoplay) {
        this.video.play()
      }
      else {
        this.video.pause()
      }
    }
    if (this.props.sourceMp4 !== prevProps.sourceMp4 || this.props.src !== prevProps.src) {
      // this.setState({ changed: true })
    }

    // Handle lazy loading state change
    if (!prevState.isVisible && this.state.isVisible && !this.state.videoLoaded && this.props.isAd) {
      this.loadVideo()
    }
  }

  componentWillUnmount() {
    if (this.videoPlayer) {
      this.videoPlayer.dispose()
      this.videoPlayer = null
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!is(nextProps.visible, this.props.visible) && nextProps.visible !== undefined) {
      this.setState({ revealed: nextProps.visible })
    }
  }

  setupIntersectionObserver = () => {
    // Don't create a new observer if one already exists
    if (this.intersectionObserver) {
      return
    }
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.state.isVisible) {
          this.setState({ isVisible: true })
          // Stop observing once visible
          this.intersectionObserver.unobserve(entry.target)
        }
      })
    }, {
      // Start loading when 10% visible, with 100px margin
      threshold: 0.1,
      rootMargin: '100px'
    })

    if (this.containerRef) {
      this.intersectionObserver.observe(this.containerRef)
    }
  }

  loadVideo = () => {
    if (this.state.videoLoaded) return
    
    this.setState({ videoLoaded: true }, () => {
      // Setup video after marking as loaded
      this.setupVideo()
    })
  }

  handleClickIfMuted() {
    if (this.videoPlayer && typeof this.props.muted !== 'undefined' && this.props.muted) {
      if(this.videoPlayer.muted()) {
        this.videoPlayer.muted(false); 
      }
    }
  }

  setPlayerRef = (n) => {
    this.player = n
    this.containerRef = n
    if (n) this.setState({ containerWidth: n.offsetWidth })
    
    // If not an ad or lazy loading is disabled, set up intersection observer
    if (this.props.isAd && this.props.lazyLoad !== false && n && !this.intersectionObserver) {
      this.setupIntersectionObserver()
    }
  }

  setVideoRef = (n) => {
    this.video = n
    // Only setup video immediately if not an ad or if lazy loading is disabled
    if (!this.props.isAd || this.props.lazyLoad === false || this.state.videoLoaded) {
      this.setupVideo()
    }
  }

  setupVideo = () => {
    if (!this.video) return null
    
    // For ads with lazy loading enabled, don't setup until visible and loaded
    if (this.props.isAd && this.props.lazyLoad !== false && !this.state.videoLoaded) {
      return null
    }

    // Check if video.js is already initialized on this element
    if (this.videoPlayer) {

      if (this.props.isAd) {
        this.videoPlayer.bigPlayButton.hide()
        this.video.play()
      }
      return null
    }

    const {
      autoplay,
      fileContentType,
      muted,
      sourceMp4,
      lowresMp4,
      src,
      shouldStopAllOtherPlayers,
      controls,
      isAd,
    } = this.props

    let srcs = [
      {
        src: sourceMp4,
        type: 'video/mp4',
        label: 'HD',
        res: 720,
        selected: window.videoQuality === 'HD',
      }
    ]

    if (lowresMp4) {
      srcs.push({
        src: lowresMp4,
        type: 'video/mp4',
        label: 'SD',
        res: 360,
        selected: window.videoQuality === 'SD',
      })
    }
    
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For ads, ensure they're muted and handle autoplay carefully
    let finalAutoplay = autoplay
    let finalMuted = muted
    
    if (isAd) {
      finalMuted = true // Always mute ads
      // Only autoplay ads if they've been lazy loaded or lazy loading is disabled
      if (this.props.lazyLoad !== false && !this.state.isVisible) {
        finalAutoplay = undefined
      }
    }

    const makeMuted = isSafari ? (typeof finalMuted !== 'undefined' || typeof finalAutoplay !== 'undefined') ? true : undefined : typeof finalMuted !== 'undefined' ? true : undefined

    this.videoPlayer = videojs(this.video, getVideoJSOptions({
      autoplay: typeof finalAutoplay !== 'undefined' ? 'play' : undefined,
      muted: typeof finalMuted !== 'undefined' ? true : undefined,
      controls: typeof controls !== 'undefined' ? true : undefined,
      sources: srcs,
      volume: window.videoVolume,
    }))

    this.videoPlayer.controlBar.addChild('QualitySelector', {}, 15)

    if (!isTouchable) {
      let fullscreen = this.videoPlayer.controlBar.getChild("FullscreenToggle")
      let index = this.videoPlayer.controlBar.children().indexOf(fullscreen)
      let downloadBtn = this.videoPlayer.controlBar.addChild('button', {}, index)
      let downloadBtnEl = downloadBtn.el()
      downloadBtnEl.style.cursor = 'pointer'
      downloadBtnEl.innerHTML = `<i style="font-size:20px;transform:rotateZ(180deg) translateY(2px);" class="${[_s.d, _s.cWhite, _s.gfi, _s['gfi-share']].join(' ')}"></i>`
      downloadBtn.controlText('Download video')
      downloadBtnEl.onclick = function() {
        const href = `${sourceMp4 || src}`.replace('media.', '')
        const a = document.createElement('a')
        a.href = href
        const fileName = sourceMp4.split('/').pop()
        a.download = `gab-video-${fileName}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    }

    if (controls === false) {
      this.videoPlayer.controls(false)
    }

    this.shouldStopAllOtherPlayers = shouldStopAllOtherPlayers
    this.videoPlayer.on('play', () => {
      if (this.shouldStopAllOtherPlayers) {
        this.stopAllOtherPlayers()
      }
      if (window.videoVolume) {
        this.videoPlayer.volume(window.videoVolume)
      }
      let cr = this.videoPlayer.currentSources()
      let targetLabel;
      cr.forEach((x) => {
        if (x.selected) {
          targetLabel = x.label
        }
      })
      if (window.videoQuality != targetLabel) {
        window.videoQuality = targetLabel
        localStorage.setItem('quality', targetLabel)
      }

      // Track ad play events for bandwidth monitoring
      if (this.props.isAd && this.props.onAdPlay) {
        this.props.onAdPlay({
          adId: this.props.adId,
          videoUrl: sourceMp4 || src,
          quality: targetLabel
        })
      }
    })

    this.videoPlayer.on('pause', () => {
      if (window.videoVolume != this.videoPlayer.volume()) {
        window.videoVolume = this.videoPlayer.volume()
        localStorage.setItem('volume', this.videoPlayer.volume())
      }
    })

    this.videoPlayer.on('ended', () => {
      if (window.videoVolume != this.videoPlayer.volume()) {
        window.videoVolume = this.videoPlayer.volume()
        localStorage.setItem('volume', this.videoPlayer.volume())
      }
    })

    // Track loading progress for bandwidth monitoring
    this.videoPlayer.on('progress', () => {
      if (this.props.isAd && this.props.onAdLoadProgress) {
        const buffered = this.videoPlayer.buffered()
        if (buffered.length > 0) {
          const percentLoaded = (buffered.end(0) / this.videoPlayer.duration()) * 100
          this.props.onAdLoadProgress({
            adId: this.props.adId,
            percentLoaded,
            bytesLoaded: buffered.end(0)
          })
        }
      }
    })
  }

  handleClickRoot = (e) => {
    if (this.props.autoplay) e.stopPropagation()
  }

  toggleReveal = () => {
    if (this.props.onToggleVisibility) {
      this.props.onToggleVisibility()
    } else {
      this.setState({ revealed: !this.state.revealed })
    }
  }

  stopAllOtherPlayers = () => {
    if (!this.props.shouldStopAllOtherPlayers) return

    const thisPlayerId = this.videoPlayer?.id()
    const allPlayers = document.querySelectorAll('.video-js')

    allPlayers.forEach((playerEl) => {
      const playerElId = playerEl.getAttribute('id')
      if (playerElId !== thisPlayerId) {
        const otherVideo = playerEl.querySelector('video')
        if (otherVideo) otherVideo.pause()
      }
    })
  }

  render() {
    const {
      src,
      inline,
      startTime,
      autoplay,
      alt,
      detailed,
      sensitive,
      aspectRatio,
      className,
      blurhash,
      loop,
      size,
      width,
      height,
      fileContentType,
      preview,
      isAd,
      lazyLoad,
    } = this.props
    const {
      revealed,
      containerWidth,
      isVisible,
      videoLoaded,
    } = this.state

    // Determine preload behavior
    let preload = 'none' // Default to none for bandwidth conservation
    
    if (!isAd) {
      // Non-ads use original logic
      preload = startTime ? 'auto' : (detailed ? 'metadata' : 'none')
    } else {
      // Ads only preload after being visible (if lazy loading enabled)
      if (lazyLoad === false) {
        preload = 'metadata' // Allow some preloading if lazy loading disabled
      } else if (isVisible && videoLoaded) {
        preload = 'metadata'
      } else {
        preload = 'none'
      }
    }

    if (!revealed && sensitive) {
      return (
        <SensitiveMediaItem
          onClick={this.toggleReveal}
          message='The author of this gab has added a warning to this video.'
          btnTitle='View'
        />
      )
    }

    const containerStyles = CX(className, {
      d: 1,
      w100PC: 1,
      h100PC: 1,
      outlineNone: 1,
      overflowHidden: 1,
    })

    // Check if preview URL is valid (not a webm extension and exists)
    const hasValidPreview = preview && !preview.endsWith('.webm') && preview.indexOf('missing') === -1
    let posterUrl = hasValidPreview ? preview : undefined
    
    // For ads that haven't loaded yet, always show poster
    if (isAd && lazyLoad !== false && !videoLoaded) {
      if (!posterUrl) {
        // You might want to use a default ad placeholder here
        posterUrl = '/path/to/default-ad-placeholder.jpg'
      }
    } else if (!posterUrl) {
      preload = 'auto'
    }

    const isInModal = detailed || className?.includes('media-gallery-item-in-modal')
    
    const style = {
      position: 'relative',
      width: '100%',
      height: isInModal ? '90vh' : '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }

    const videoStyle = {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    }

    const playerStyle = {
      width: '100%',
      height: '100%',
      position: 'relative',
      ...(isInModal ? { height: '90vh' } : {})
    }

    // Show loading placeholder for ads that haven't loaded yet
    const showVideoElement = !isAd || lazyLoad === false || videoLoaded

    return (
      <div
        className={containerStyles}
        style={style}
        ref={this.setPlayerRef}
        onClick={this.handleClickRoot}
        tabIndex={0}
        key={src}
      >
        <div data-vjs-player style={playerStyle}>
          {blurhash && (
            <div id='vjs-blurhash' className={[_s.d, _s.top0, _s.right0, _s.bottom0, _s.left0, _s.posAbs, _s.h100PC, _s.w100PC, _s.z1].join(' ')}>
              <Blurhash hash={blurhash} className={[_s.d, _s.top0, _s.right0, _s.bottom0, _s.left0, _s.posAbs, _s.h100PC, _s.w100PC].join(' ')} />
            </div>
          )}
          
          {/* Show loading state for ads */}
          {isAd && lazyLoad !== false && !videoLoaded && (
            <div 
              className={[_s.d, _s.h100PC, _s.w100PC, _s.aiCenter, _s.jcCenter, _s.bgSecondary].join(' ')}
              style={{ 
                backgroundImage: posterUrl ? `url(${posterUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {!posterUrl && (
                <div className={[_s.d, _s.aiCenter, _s.jcCenter, _s.cSecondary].join(' ')}>
                  Loading ad...
                </div>
              )}
            </div>
          )}

          {showVideoElement && (
            <video
              className={[_s.d, _s.h100PC, _s.w100PC, _s.outlineNone, 'video-js'].join(' ')}
              ref={this.setVideoRef}
              playsInline
              loop={loop}
              poster={posterUrl}
              preload={preload}
              role='button'
              tabIndex='0'
              aria-label={alt}
              title={alt}
              style={videoStyle}
              onClick={this.handleClickIfMuted.bind(this)}
            />
          )}
        </div>

        {revealed && sensitive &&
          <div className={[_s.posAbs, _s.z2, _s.top0, _s.right0, _s.mt10, _s.mr10].join(' ')}>
            <Button
              title='Hide media'
              icon='hidden'
              backgroundColor='black'
              className={[_s.px10, _s.bgBlackOpaque_onHover].join(' ')}
              onClick={this.toggleReveal}
            />
          </div>
        }
      </div>
    )
  }
}

Video.propTypes = {
  preview: PropTypes.string,
  sourceMp4: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  sensitive: PropTypes.bool,
  startTime: PropTypes.number,
  detailed: PropTypes.bool,
  inline: PropTypes.bool,
  visible: PropTypes.bool,
  onToggleVisibility: PropTypes.func,
  blurhash: PropTypes.string,
  classes: PropTypes.array,
  aspectRatio: PropTypes.number,
  meta: ImmutablePropTypes.map,
  fileContentType: PropTypes.string,
  autoplay: PropTypes.string,
  muted: PropTypes.string,
  shouldStopAllOtherPlayers: PropTypes.bool,
  controls: PropTypes.bool,
  loop: PropTypes.bool,
  size: PropTypes.number,
  // New props for ad handling
  isAd: PropTypes.bool,
  lazyLoad: PropTypes.bool, // Set to false to disable lazy loading
  adId: PropTypes.string,
  onAdPlay: PropTypes.func,
  onAdLoadProgress: PropTypes.func,
}

Video.defaultProps = {
  isAd: false,
  lazyLoad: true,
}

export default Video